import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Card,
  CardContent,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  Security,
  Info,
  CheckCircle,
  Warning
} from '@mui/icons-material';
import { depositService } from '../services/depositService';
import { useAuth } from '../contexts/AuthContext';

interface DepositValidationDialogProps {
  open: boolean;
  onClose: () => void;
  depositAmount: number;
  dressTitle: string;
  bookingId?: string;
  onValidate: (accepted: boolean) => void;
}

const DepositValidationDialog: React.FC<DepositValidationDialogProps> = ({
  open,
  onClose,
  depositAmount,
  dressTitle,
  bookingId,
  onValidate
}) => {
  const { user } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [validated, setValidated] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleValidate = async (accepted: boolean) => {
    if (!user) {
      setError('Utilisateur non connecté');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Si bookingId est fourni, valider via l'API
      if (bookingId) {
        const result = await depositService.validateDeposit(bookingId, user.uid, accepted);

        if (result.success) {
          setValidated(true);
          onValidate(accepted);
        } else {
          setError(result.error || 'Erreur lors de la validation du dépôt');
        }
      } else {
        // Si pas de bookingId (réservation pas encore créée), valider localement
        // La validation sera enregistrée lors de la création de la réservation
        setValidated(true);
        onValidate(accepted);
      }
    } catch (err) {
      console.error('Erreur validation dépôt:', err);
      setError('Erreur de connexion au serveur');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAccept = () => {
    handleValidate(true);
  };

  const handleReject = () => {
    handleValidate(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Security sx={{ mr: 1, color: '#a5816d' }} />
          Validation de la caution
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {dressTitle}
          </Typography>
          <Divider sx={{ my: 2 }} />
          
          <Card sx={{ backgroundColor: '#f5f5f5', mb: 2 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Montant de la caution
              </Typography>
              <Typography variant="h4" sx={{ color: '#a5816d', fontWeight: 'bold' }}>
                {depositAmount}€
              </Typography>
            </CardContent>
          </Card>

          <Alert severity="info" sx={{ mb: 2 }}>
            <Info sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
            <Typography variant="body2">
              <strong>Important:</strong> La caution sera payée 3 jours avant le début de la location 
              et sera remboursée automatiquement 7 jours après le paiement si aucun problème n'est signalé.
            </Typography>
          </Alert>

          <Alert severity="warning">
            <Warning sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
            <Typography variant="body2">
              En cas de litige (dommages, perte, etc.), la caution sera versée au propriétaire 
              (100% ou un pourcentage selon la gravité des dégâts).
            </Typography>
          </Alert>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2">{error}</Typography>
          </Alert>
        )}

        {validated && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <CheckCircle sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
            <Typography variant="body2">
              Caution validée avec succès. Le paiement sera effectué automatiquement 3 jours avant la location.
            </Typography>
          </Alert>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} disabled={isProcessing || validated}>
          {validated ? 'Fermer' : 'Annuler'}
        </Button>
        {!validated && (
          <>
            <Button
              variant="outlined"
              onClick={handleReject}
              disabled={isProcessing}
              sx={{ mr: 1 }}
            >
              Refuser
            </Button>
            <Button
              variant="contained"
              onClick={handleAccept}
              disabled={isProcessing}
              sx={{ backgroundColor: '#a5816d', '&:hover': { backgroundColor: '#8B7355' } }}
            >
              {isProcessing ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Valider la caution'
              )}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DepositValidationDialog;

